import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import { documentsV2Api } from "api/documentsV2Api";
import { useDocumentTypesOptions } from "api/hooks";
import { PaziaFormButton, PaziaInput, PaziaLoader, PaziaSelect } from "components/atoms";
import { HeaderWithBreadcrumbs, PageMain } from "components/molecules";
import { routes } from "consts";
import { PAGE_SIZE_PRESETS, createEmptyTemplate } from "documentsV2/pdfme/pageSizes";
import { PDFME_VERSION_PINNED } from "documentsV2/pdfme/version";
import { QUERIES_KEYS } from "queries";
import { getApiErrorMessage, useToaster } from "utils";

const CUSTOM_PAGE_SIZE = "custom";
const MIN_SIDE_MM = 20;
const MAX_SIDE_MM = 1000;

const PAGE_SIZE_OPTIONS = [
  ...PAGE_SIZE_PRESETS.map((preset) => ({ value: preset.key, label: preset.label })),
  { value: CUSTOM_PAGE_SIZE, label: "Własny" },
];

/** Creates an empty "Dokumenty 2.0" template (blank page of the chosen size) and opens it in the editor. */
export const AddDocumentTemplateV2Page = (): JSX.Element => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToaster();
  const { loading, options: documentTypeOptions } = useDocumentTypesOptions();

  const methods = useForm<AddTemplateFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      [FORM_NAMES.NAME]: "",
      [FORM_NAMES.PAGE_SIZE]: PAGE_SIZE_PRESETS[0].key,
    },
  });
  const { handleSubmit, watch } = methods;
  const isCustomSize = watch(FORM_NAMES.PAGE_SIZE) === CUSTOM_PAGE_SIZE;

  const onSubmit = async (values: AddTemplateFormValues) => {
    const preset = PAGE_SIZE_PRESETS.find((p) => p.key === values.pageSize);
    const widthMm = preset ? preset.width : values.width!;
    const heightMm = preset ? preset.height : values.height!;

    try {
      const templateId = await documentsV2Api.create({
        name: values.name.trim(),
        documentTypeId: values.documentTypeId,
        templateJson: JSON.stringify(createEmptyTemplate(widthMm, heightMm)),
        pdfmeVersion: PDFME_VERSION_PINNED,
      });
      await queryClient.invalidateQueries({ queryKey: QUERIES_KEYS.DOCUMENT_TEMPLATES_V2_ROOT() });
      showSuccess("Utworzono szablon");
      navigate(routes.DOCUMENTS_V2_EDITOR(templateId));
    } catch (error) {
      showError(getApiErrorMessage(error, "Nie udało się utworzyć szablonu"));
    }
  };

  const breadcrumbs = [{ title: "Szablony dokumentów 2.0", path: routes.DOCUMENTS_V2 }, { title: "Nowy szablon" }];

  return (
    <PageMain>
      <HeaderWithBreadcrumbs breadcrumbs={breadcrumbs} headerText="Dodaj szablon 2.0" />
      {loading ? (
        <PaziaLoader position="center" />
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="flex max-w-xl flex-col gap-4 p-4">
            <PaziaInput name={FORM_NAMES.NAME} label="Nazwa" type="text" required />
            <PaziaSelect
              name={FORM_NAMES.DOCUMENT_TYPE_ID}
              label="Typ dokumentu"
              options={documentTypeOptions}
              required
            />
            <PaziaSelect
              name={FORM_NAMES.PAGE_SIZE}
              label="Rozmiar strony"
              options={PAGE_SIZE_OPTIONS}
              isClearable={false}
              required
            />
            {isCustomSize && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <PaziaInput
                  name={FORM_NAMES.WIDTH}
                  label={`Szerokość [mm] (${MIN_SIDE_MM}–${MAX_SIDE_MM})`}
                  type="number"
                  step="any"
                  required
                />
                <PaziaInput
                  name={FORM_NAMES.HEIGHT}
                  label={`Wysokość [mm] (${MIN_SIDE_MM}–${MAX_SIDE_MM})`}
                  type="number"
                  step="any"
                  required
                />
              </div>
            )}
            <div className="flex justify-end">
              <PaziaFormButton title="Utwórz szablon" allowCleanSubmit />
            </div>
          </form>
        </FormProvider>
      )}
    </PageMain>
  );
};

const FORM_NAMES = {
  NAME: "name",
  DOCUMENT_TYPE_ID: "documentTypeId",
  PAGE_SIZE: "pageSize",
  WIDTH: "width",
  HEIGHT: "height",
} as const;

// A blank number input is "" in the DOM; read it as "not given" instead of a type error. The field's error line turns
// required/min errors into its own generic Polish text, so only the max message is worth writing; the label shows the range.
const customSide = () =>
  yup
    .number()
    .transform((value, original) => (original === "" || original === null ? undefined : value))
    .when(FORM_NAMES.PAGE_SIZE, {
      is: CUSTOM_PAGE_SIZE,
      then: (side) =>
        side.required().min(MIN_SIDE_MM).max(MAX_SIDE_MM, `Maksymalnie ${MAX_SIDE_MM} mm.`),
      otherwise: (side) => side.notRequired(),
    });

const schema = yup
  .object({
    [FORM_NAMES.NAME]: yup.string().trim().required(),
    // The select hands over the option value (a string); yup turns it into the id.
    [FORM_NAMES.DOCUMENT_TYPE_ID]: yup.number().required(),
    [FORM_NAMES.PAGE_SIZE]: yup.string().required(),
    [FORM_NAMES.WIDTH]: customSide(),
    [FORM_NAMES.HEIGHT]: customSide(),
  })
  .required();

type AddTemplateFormValues = yup.InferType<typeof schema>;
